import {
  Card,
  Banner,
  InputSelectControlField,
  Skeleton,
  GreyGrey10,
  Primary14SemiBold,
  Box,
  Flex
} from '@sequencehq/core-components'
import { ConfigurationManagementProps } from 'Integrations/domain'
import { useXeroConfigurationManagement } from 'modules/Integrations/integrationsConfig/xero/XeroConfigurationManagement/useXeroConfigurationManagement'
import { AutoLinkCustomersCard } from 'Integrations/integrationsConfig/common/AutoLinkCustomers/AutoLinkCustomersCard'
import { IntegrationActiveToggle } from 'Integrations/integrationsConfig/common/IntegrationActiveToggle'
import { PaymentBankAccountsCard } from 'Integrations/integrationsConfig/common/PaymentBankAccounts/PaymentBankAccountsCard'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { MINUTES } from '@sequencehq/utils/dates'

export const XeroConfigurationManagement = (
  props: ConfigurationManagementProps
) => {
  const {
    data: fullIntegration,
    isLoading,
    isError
  } = useQuery(dashboard20240730Client.getIntegration, 'Xero', {
    staleTime: 1 * MINUTES,
    enabled: props.integration.hasWorkingConnection
  })
  const bankAccounts =
    props.integration.hasWorkingConnection &&
    fullIntegration?.service === 'Xero'
      ? fullIntegration.bankAccounts.map(account => ({
          ...account,
          name: account.name
            ? `${account.name} (${account.code})`
            : account.code
        }))
      : []

  const {
    fieldsConfig,
    loading,
    autoLinkPaused,
    showTaxLiabilityLedgerAccount
  } = useXeroConfigurationManagement(props)

  if (loading) {
    return (
      <Flex flexDirection="column" gap="20px" width="100%">
        <Skeleton width="100%" height="58px" />
        <Skeleton width="100%" height="154px" />
        <Skeleton width="100%" height="154px" />
      </Flex>
    )
  }

  return (
    <Flex
      flexDirection="column"
      gap="20px"
      width="100%"
      data-testid="xeroConfiguration"
    >
      <IntegrationActiveToggle
        checked={fieldsConfig.importCustomerWorkflow.value}
        active={fieldsConfig.importCustomerWorkflow.status === 'ACTIVE'}
        onChange={fieldsConfig.importCustomerWorkflow.onChange}
      />
      <AutoLinkCustomersCard
        service="Xero"
        value={fieldsConfig.autoLinkCustomers.value}
        paused={autoLinkPaused}
        onChange={fieldsConfig.autoLinkCustomers.onChange}
      />
      <Card>
        <Box>
          <Box {...Primary14SemiBold} pb="16px">
            Default ledger accounts
          </Box>
          <Flex
            backgroundColor={GreyGrey10}
            borderRadius="6px"
            padding="16px"
            gap={4}
            flexWrap="wrap"
          >
            <InputSelectControlField
              styles={{
                wrapper: {
                  marginBottom: 0,
                  maxWidth: '336px'
                }
              }}
              placeholder="Select an account"
              data-testid="select.ledgerAccount"
              options={fieldsConfig.defaultLedgerAccount.options}
              label="Default ledger account"
              initialValue={fieldsConfig.defaultLedgerAccount.value}
              onChange={fieldsConfig.defaultLedgerAccount.onChange}
              validationErrors={
                fieldsConfig.defaultLedgerAccount.validationErrors
              }
              disabled={fieldsConfig.defaultLedgerAccount.disabled}
              matchWidth
            />
            <InputSelectControlField
              styles={{
                wrapper: {
                  marginBottom: 0,
                  maxWidth: '336px'
                }
              }}
              placeholder="Select an account"
              data-testid="select.creditLedgerAccount"
              label="Default deferred income ledger account"
              initialValue={fieldsConfig.defaultCreditLedgerAccount.value}
              options={fieldsConfig.defaultCreditLedgerAccount.options}
              onChange={fieldsConfig.defaultCreditLedgerAccount.onChange}
              validationErrors={
                fieldsConfig.defaultCreditLedgerAccount.validationErrors
              }
              disabled={fieldsConfig.defaultCreditLedgerAccount.disabled}
              matchWidth
            />
            <InputSelectControlField
              styles={{
                wrapper: {
                  marginBottom: 0,
                  maxWidth: '336px'
                }
              }}
              placeholder="Select an account"
              data-testid="select.minimumLedgerAccount"
              label="Default ledger account for minimums"
              initialValue={fieldsConfig.defaultMinimumLedgerAccount.value}
              options={fieldsConfig.defaultMinimumLedgerAccount.options}
              onChange={fieldsConfig.defaultMinimumLedgerAccount.onChange}
              validationErrors={
                fieldsConfig.defaultMinimumLedgerAccount.validationErrors
              }
              disabled={fieldsConfig.defaultMinimumLedgerAccount.disabled}
              matchWidth
            />
            {showTaxLiabilityLedgerAccount && (
              <InputSelectControlField
                styles={{
                  wrapper: {
                    marginBottom: 0,
                    maxWidth: '336px'
                  }
                }}
                placeholder="Select an account"
                data-testid="select.taxLiabilityLedgerAccount"
                label="Tax liability ledger account"
                initialValue={
                  fieldsConfig.defaultTaxLiabilityLedgerAccount.value
                }
                options={fieldsConfig.defaultTaxLiabilityLedgerAccount.options}
                onChange={
                  fieldsConfig.defaultTaxLiabilityLedgerAccount.onChange
                }
                validationErrors={
                  fieldsConfig.defaultTaxLiabilityLedgerAccount.validationErrors
                }
                disabled={
                  fieldsConfig.defaultTaxLiabilityLedgerAccount.disabled
                }
                infoPopover={{
                  body: 'Posts US and Canadian sales tax to this account as a line item. Leave unset to use the default rate.'
                }}
                matchWidth
              />
            )}
          </Flex>
        </Box>
      </Card>
      <Card>
        <Box>
          <Box {...Primary14SemiBold} pb="16px">
            Tax codes
          </Box>
          <Flex
            backgroundColor={GreyGrey10}
            borderRadius="6px"
            padding="16px"
            gap={4}
            flexWrap="wrap"
          >
            <InputSelectControlField
              styles={{
                wrapper: {
                  marginBottom: 0,
                  maxWidth: '336px'
                }
              }}
              placeholder="Select a tax code"
              data-testid="select.untaxedCustomerTaxCode"
              options={fieldsConfig.untaxedCustomerTaxCode.options}
              label="Tax exempt and reverse charged customers"
              initialValue={fieldsConfig.untaxedCustomerTaxCode.value}
              onChange={fieldsConfig.untaxedCustomerTaxCode.onChange}
              validationErrors={
                fieldsConfig.untaxedCustomerTaxCode.validationErrors
              }
              disabled={fieldsConfig.untaxedCustomerTaxCode.disabled}
              infoPopover={{
                body: 'Applies to tax exempt and reverse charged customers. All others use the default tax code set in Xero for their ledger account.'
              }}
              matchWidth
            />
          </Flex>
        </Box>
      </Card>
      {props.integration.hasWorkingConnection && isLoading ? (
        <Skeleton
          data-testid="xeroIntegration.paymentBankAccounts.loading"
          width="100%"
          height="154px"
        />
      ) : props.integration.hasWorkingConnection && isError ? (
        <Banner
          variant="error"
          data-testid="xeroIntegration.paymentBankAccounts.error"
        >
          Could not load Xero bank accounts. Refresh the page to try again.
        </Banner>
      ) : (
        <PaymentBankAccountsCard
          data-testid="xeroIntegration.paymentBankAccounts"
          serviceName="Xero"
          allowClearing
          fallbackDescription="Set an account for pushing payments in each currency. If no account is selected, Xero uses its only active bank account. Select one when multiple accounts are active."
          bankAccounts={bankAccounts}
          value={fieldsConfig.paymentBankAccounts.value}
          onChange={fieldsConfig.paymentBankAccounts.onChange}
        />
      )}
    </Flex>
  )
}
